/** The map-object payload primitives, and nothing that decides a rule.
 *
 * Split out of `objects.ts` so that the visit handlers can live in more than
 * one file without importing each other in a circle: `objects.ts` owns the
 * dispatch and re-exports everything here, `services.ts` and `quests.ts` own a
 * branch of it and import from here only. Nothing in this file reads a payload
 * key by name - it is the vocabulary, not the grammar.
 */

import type { HeroId, ObjectId, PlayerId } from "../ids.js";
import type { Reason } from "../commands.js";
import type { MapObjectState } from "../state.js";
import { indexOf, setOccupant } from "../map/create.js";
import type { Ctx } from "./ctx.js";
import { patchObject } from "./ctx.js";

// --------------------------------------------------------------- outcomes

export type VisitOutcome =
  /** Resolved; a hero mid-path keeps walking. */
  | { readonly kind: "done" }
  /** Resolved, but the object ends the hero's movement (boat, portal, town). */
  | { readonly kind: "stop" }
  /** The hero may not enter this cell at all. */
  | { readonly kind: "blocked"; readonly reason: Reason }
  /** A prompt is open; the move resumes from the token. */
  | { readonly kind: "pending" }
  /** A battle started; the classic cancels the queued path either way. */
  | { readonly kind: "battle"; readonly object: ObjectId };

export const DONE: VisitOutcome = Object.freeze({ kind: "done" });
export const STOP: VisitOutcome = Object.freeze({ kind: "stop" });

// ----------------------------------------------------------------- payload

export type Payload = Readonly<Record<string, number | string | boolean | null>>;

export function payloadOf(c: Ctx, o: MapObjectState): Payload {
  const def = c.content.mapObjects.get(o.def);
  return def === undefined ? o.payload : { ...def.payload, ...o.payload };
}

export const payNum = (p: Payload, k: string, fallback = 0): number => {
  const v = p[k];
  return typeof v === "number" ? v : fallback;
};
export const payStr = (p: Payload, k: string, fallback = ""): string => {
  const v = p[k];
  return typeof v === "string" ? v : fallback;
};
export const payFlag = (p: Payload, k: string): boolean => {
  const v = p[k];
  return v === true || v === 1;
};

/**
 * A comma-separated payload list, empty entries dropped.
 *
 * State is plain data (I7) and a payload value is a scalar, so a list has to be
 * a string. Comma is the separator everywhere and a colon separates a pair, so
 * `"ballista:2500,supply_wagon:1000"` is the one shape every offer list uses.
 */
export const payList = (p: Payload, k: string): readonly string[] =>
  payStr(p, k).split(",").map((s) => s.trim()).filter((s) => s.length > 0);

/** `"id:number"` split into its two halves; a missing number reads as `fallback`. */
export function payPair(entry: string, fallback = 0): { readonly id: string; readonly n: number } {
  const sep = entry.indexOf(":");
  if (sep < 0) return { id: entry, n: fallback };
  const n = Number.parseInt(entry.slice(sep + 1), 10);
  return { id: entry.slice(0, sep), n: Number.isFinite(n) ? n : fallback };
}

export function setPayload(
  c: Ctx, id: ObjectId, patch: Readonly<Record<string, number | string | boolean | null>>,
): void {
  const o = c.s.objects[id];
  if (o === undefined) return;
  const merged: Record<string, number | string | boolean | null> = {};
  for (const k of Object.keys({ ...o.payload, ...patch }).sort()) {
    merged[k] = patch[k] ?? o.payload[k] ?? null;
  }
  patchObject(c, id, { payload: merged });
}

// ------------------------------------------------------------- visit marks

/**
 * Per-player visit marks live in the object's own payload under `p:<playerId>`.
 *
 * They have to outlive the hero that made them - a Keymaster's Tent stays
 * visited when the scout who found it dies, and a puzzle obelisk counts toward
 * the player, not the hero - and `MapObjectState.visitedBy` is a hero list.
 */
export const playerMarkKey = (p: PlayerId): string => `p:${p}`;

export const playerVisited = (c: Ctx, o: MapObjectState, p: PlayerId): boolean =>
  payloadOf(c, o)[playerMarkKey(p)] === true;

export const markPlayerVisit = (c: Ctx, id: ObjectId, p: PlayerId): void =>
  setPayload(c, id, { [playerMarkKey(p)]: true });

export function heroVisited(o: MapObjectState, h: HeroId): boolean {
  return o.visitedBy.includes(h);
}

// --------------------------------------------------------------- removal

export function removeObject(c: Ctx, id: ObjectId): void {
  const o = c.s.objects[id];
  if (o === undefined || o.removed) return;
  patchObject(c, id, { removed: true });
  const cell = indexOf(c.s.map, o.x, o.y, o.z);
  if (cell >= 0) setOccupant(c.s.map, o.x, o.y, o.z, null, false);
  c.events.push({ type: "ObjectRemoved", object: id });
}
